/**
 * WhatsApp conversations hook — contact threads (inbound + outbound) and replies,
 * backed by /api/whatsapp/conversations. `resp.data` is the unwrapped payload.
 */

import { useState, useCallback } from 'react';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';
import { toast } from '../../../../components/ui/providers/AlertProvider';

export interface ConversationSummary {
  contactNumber: string;
  contactName?: string | null;
  email?: string | null;
  isMember?: boolean;
  lastMessage?: string | null;
  lastDirection?: string | null;
  lastActivity: number;
  inboundCount: number;
  outboundCount: number;
  unreadCount: number;
}

export interface ConversationMessage {
  id: number;
  direction: string; // Inbound | Outbound
  body?: string | null;
  type?: string | null;
  status?: string | null;
  timestamp: number;
}

export interface ConversationThread {
  contactNumber: string;
  contactName?: string | null;
  email?: string | null;
  isMember?: boolean;
  messages: ConversationMessage[];
}

const path = (p: string) => getApiUrl('communications', `/api/whatsapp/conversations${p}`);

export function useWhatsAppConversations() {
  const httpClient = useHttpClient();
  const [loadingList, setLoadingList] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [thread, setThread] = useState<ConversationThread | null>(null);

  const fetchConversations = useCallback(async () => {
    setLoadingList(true);
    setError(null);
    try {
      const resp = await httpClient.get<ConversationSummary[]>(path('?limit=200'));
      setConversations((resp.data as ConversationSummary[]) ?? []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load conversations');
    } finally {
      setLoadingList(false);
    }
  }, [httpClient]);

  const fetchThread = useCallback(async (contactNumber: string) => {
    setLoadingThread(true);
    try {
      const resp = await httpClient.get<ConversationThread>(path(`/${contactNumber}`));
      setThread((resp.data as ConversationThread) ?? null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load conversation');
    } finally {
      setLoadingThread(false);
    }
  }, [httpClient]);

  const reply = useCallback(async (contactNumber: string, text: string) => {
    setSending(true);
    try {
      await httpClient.post(path(`/${contactNumber}/reply`), { text });
      toast.success('Reply sent');
      await fetchThread(contactNumber);
      return true;
    } catch (err: any) {
      toast.error(err?.message || 'Reply failed (outside the 24h window? send a template)');
      return false;
    } finally {
      setSending(false);
    }
  }, [httpClient, fetchThread]);

  const markRead = useCallback(async (contactNumber: string) => {
    try {
      await httpClient.post(path(`/${contactNumber}/read`), {});
      await fetchConversations();
    } catch {
      /* non-critical */
    }
  }, [httpClient, fetchConversations]);

  return {
    loadingList, loadingThread, sending, error,
    conversations, thread,
    fetchConversations, fetchThread, reply, markRead,
  };
}
